import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useLocalStorage } from '@/hooks/use-local-storage';

export const SIDEBAR_STORAGE_KEY = 'ribox-crm.sidebar-collapsed';

export interface ShellState {
  collapsed: boolean;
  toggleCollapsed: () => void;
  mobileNavOpen: boolean;
  setMobileNavOpen: (open: boolean) => void;
  paletteOpen: boolean;
  setPaletteOpen: (open: boolean) => void;
  shortcutsOpen: boolean;
  setShortcutsOpen: (open: boolean) => void;
}

const ShellContext = createContext<ShellState | null>(null);

/** Chrome state only: what is open and how wide. Nothing about the data below. */
export function ShellProvider({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useLocalStorage<boolean>(SIDEBAR_STORAGE_KEY, false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const toggleCollapsed = useCallback(() => setCollapsed((prev) => !prev), [setCollapsed]);

  const value = useMemo<ShellState>(
    () => ({
      collapsed,
      toggleCollapsed,
      mobileNavOpen,
      setMobileNavOpen,
      paletteOpen,
      setPaletteOpen,
      shortcutsOpen,
      setShortcutsOpen,
    }),
    [collapsed, toggleCollapsed, mobileNavOpen, paletteOpen, shortcutsOpen],
  );

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function useShell(): ShellState {
  const value = useContext(ShellContext);
  if (!value) throw new Error('useShell() necesita un <ShellProvider> por encima.');
  return value;
}
